/* Имена по модели Gramps.
 *
 * У человека не «фамилия и имя», а СПИСОК имён: одно основное
 * (primary_name) и сколько угодно прочих (alternate_names), у каждого свой
 * тип — при рождении, в браке, известен как. Раньше мы держали два жёстких
 * поля «при рождении» и «в браке»; всё остальное, что заводил импорт или
 * штатный Gramps, в форму не попадало и правке не поддавалось.
 *
 * Внутри имени фамилия — тоже список: у Gramps их может быть несколько,
 * с приставками и типом происхождения. Мы правим одну, основную: вторые
 * фамилии в наших данных не встречаются, а полноценный их редактор
 * стоил бы половины формы.
 */

export const NAME_TYPES: [number, string][] = [
  [2, 'При рождении'],
  [3, 'В браке'],
  [4, 'Известен как'],
  [0, 'Не указан'],
]

/** Откуда взялась фамилия. В Gramps это NameOriginType. */
export const ORIGIN_TYPES: [number, string][] = [
  [1, 'не указано'],
  [2, 'наследственная'],
  [3, 'данная'],
  [4, 'принятая'],
  [5, 'отчество'],
  [6, 'матроним'],
  [8, 'псевдоним'],
  [11, 'по занятию'],
  [12, 'по месту'],
]

const NAME_BY_STRING: Record<string, number> = {
  'Birth Name': 2, 'Married Name': 3, 'Also Known As': 4, Unknown: 0,
}

export interface NameEntry {
  type: number
  /** имя и отчество: импорт GEDCOM положил их в first_name одним полем */
  first: string
  surname: string
  /** приставка фамилии: «де», «фон», «ван» */
  prefix: string
  origin: number
  suffix: string
  title: string
  /** как звали в семье */
  call: string
  nick: string
  famnick: string
  /** остальные поля имени, которых нет в форме, — чтобы не стереть при записи */
  rest: Record<string, unknown>
}

export interface NameRaw {
  type?: string | {value?: number}
  first_name?: string
  suffix?: string
  title?: string
  call?: string
  nick?: string
  famnick?: string
  surname_list?: {
    surname?: string
    prefix?: string
    origintype?: string | {value?: number}
    [k: string]: unknown
  }[]
  [k: string]: unknown
}

const numType = (t: unknown, table: Record<string, number>): number => {
  if (typeof t === 'string') return table[t] ?? 0
  return (t as {value?: number} | undefined)?.value ?? 0
}

const ORIGIN_BY_STRING: Record<string, number> = {
  Unknown: 1, Inherited: 2, Given: 3, Taken: 4, Patronymic: 5, Matronymic: 6,
  Feudal: 7, Pseudonym: 8, Patrilineal: 9, Matrilineal: 10, Occupation: 11,
  Location: 12,
}

export const nameTypeRu = (v: number): string =>
  NAME_TYPES.find(([x]) => x === v)?.[1] || 'Не указан'

export function nameFromRaw(n: NameRaw): NameEntry {
  const s = n.surname_list?.[0] || {}
  const {surname_list, type, first_name, suffix, title, call, nick, famnick, ...rest} = n
  return {
    type: numType(type, NAME_BY_STRING),
    first: first_name || '',
    surname: s.surname || '',
    prefix: s.prefix || '',
    origin: numType(s.origintype, ORIGIN_BY_STRING) || 1,
    suffix: suffix || '',
    title: title || '',
    call: call || '',
    nick: nick || '',
    famnick: famnick || '',
    rest,
  }
}

export const emptyName = (type = 3): NameEntry => ({
  type, first: '', surname: '', prefix: '', origin: 1,
  suffix: '', title: '', call: '', nick: '', famnick: '',
  rest: {},
})

export const nameIsEmpty = (n: NameEntry) =>
  !n.first.trim() && !n.surname.trim() && !n.nick.trim() && !n.call.trim() && !n.title.trim()

/** Как имя выглядит в строке: «Воронько Виктор Иванович (Витя)». */
export function nameLabel(n: NameEntry): string {
  const base = [n.prefix, n.surname].filter(Boolean).join(' ')
  const main = [base, n.first, n.suffix].map(x => x.trim()).filter(Boolean).join(' ')
  const alias = n.nick.trim() || n.call.trim()
  return [n.title.trim(), main].filter(Boolean).join(' ') + (alias ? ` (${alias})` : '')
}

/* Поля, которых нет в форме, но которые есть у объекта Name, сохраняем как
   есть: у имени бывают дата, цитаты и правила сортировки — их правят в
   штатном Gramps, и молча терять их при нашем сохранении нельзя. */
const NAME_DEFAULTS = {
  _class: 'Name',
  private: false,
  citation_list: [],
  note_list: [],
  date: {
    _class: 'Date', calendar: 0, modifier: 0, quality: 0,
    dateval: [0, 0, 0, false], text: '', sortval: 0, newyear: 0,
  },
  group_as: '',
  sort_as: 0,
  display_as: 0,
}

export function nameBody(n: NameEntry): Record<string, unknown> {
  return {
    ...NAME_DEFAULTS,
    ...n.rest,
    _class: 'Name',
    type: {_class: 'NameType', value: n.type, string: ''},
    first_name: n.first.trim(),
    suffix: n.suffix.trim(),
    title: n.title.trim(),
    call: n.call.trim(),
    nick: n.nick.trim(),
    famnick: n.famnick.trim(),
    surname_list: n.surname.trim() || n.prefix.trim()
      ? [{
          _class: 'Surname',
          surname: n.surname.trim(),
          prefix: n.prefix.trim(),
          primary: true,
          connector: '',
          origintype: {_class: 'NameOriginType', value: n.origin, string: ''},
        }]
      : [],
  }
}
